import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Paper,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Stack,
  Link,
} from "@mui/material";
import {
  Close,
  Edit,
  Person,
  CalendarToday,
  Description,
  Flag,
  CheckCircle,
  RadioButtonUnchecked,
  AttachFile,
  OpenInNew,
} from "@mui/icons-material";
import {
  TASK_STATUS_LABELS,
  TASK_STATUS_COLORS,
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_COLORS,
} from "../../utils/models/taskModel";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";

const TaskDetail = ({ task, onEdit, onClose, onRefresh }) => {
  const theme = useTheme();
  const { update } = useAuditFirestore("tasks");

  if (!task) return null;

  const handleStatusToggle = async () => {
    const newStatus = task.status === "completed" ? "pending" : "completed";
    await update(task.id, {
      status: newStatus,
      completedAt: newStatus === "completed" ? new Date().toISOString() : null,
      updatedAt: new Date().toISOString(),
    });
    onRefresh();
  };

  const isOverdue =
    task.dueDate &&
    task.status !== "completed" &&
    new Date(task.dueDate) < new Date();

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 4 },
        borderRadius: "24px",
        border: "1px solid",
        borderColor:
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#e2e8f0",
        background: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
          <Box
            sx={{
              width: 4,
              height: 40,
              borderRadius: "6px",
              backgroundColor: TASK_STATUS_COLORS[task.status],
            }}
          />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {task.title}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
              <Chip
                label={TASK_STATUS_LABELS[task.status]}
                size="small"
                color={
                  task.status === "completed"
                    ? "success"
                    : task.status === "in_progress"
                      ? "info"
                      : "warning"
                }
              />
              <Chip
                label={TASK_PRIORITY_LABELS[task.priority]}
                size="small"
                sx={{
                  backgroundColor: TASK_PRIORITY_COLORS[task.priority],
                  color: "white",
                }}
              />
              {isOverdue && <Chip label="Vencida" size="small" color="error" />}
            </Box>
          </Box>
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      <List dense disablePadding>
        {task.description && (
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Description
                fontSize="small"
                sx={{ color: theme.palette.info.main }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Descripción"
              secondary={task.description}
              primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
            />
          </ListItem>
        )}

        <ListItem sx={{ px: 0 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Flag
              fontSize="small"
              sx={{ color: TASK_PRIORITY_COLORS[task.priority] }}
            />
          </ListItemIcon>
          <ListItemText
            primary="Prioridad"
            secondary={TASK_PRIORITY_LABELS[task.priority]}
            primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
          />
        </ListItem>

        {(Array.isArray(task.assignedToNames) && task.assignedToNames.length > 0) || task.assignedToName ? (
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Person
                fontSize="small"
                sx={{ color: theme.palette.primary.main }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Asignada a"
              secondary={
                Array.isArray(task.assignedToNames) && task.assignedToNames.length > 0
                  ? task.assignedToNames.join(', ')
                  : task.assignedToName
              }
              primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
            />
          </ListItem>
        ) : null}

        {task.assignedByName && (
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Person
                fontSize="small"
                sx={{ color: theme.palette.success.main }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Asignada por"
              secondary={task.assignedByName}
              primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
            />
          </ListItem>
        )}

        {task.dueDate && (
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CalendarToday
                fontSize="small"
                sx={{
                  color: isOverdue
                    ? theme.palette.error.main
                    : theme.palette.warning.main,
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Fecha de vencimiento"
              secondary={new Date(task.dueDate).toLocaleDateString("es-ES", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
              primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
            />
          </ListItem>
        )}
      </List>

      {/* ARCHIVOS */}
      {task.files && task.files.length > 0 && (
        <>
          <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ 
              mb: 2,
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: { xs: "0.85rem", sm: "0.9rem" },
            }}
          >
            <AttachFile sx={{ fontSize: { xs: 18, sm: 20 } }} />
            Archivos adjuntos
          </Typography>
          <List dense disablePadding>
            {task.files.map((file, index) => (
              <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Description sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.info.main }} />
                </ListItemIcon>
                <ListItemText>
                  <Link
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      cursor: 'pointer',
                      wordBreak: 'break-all',
                      '&:hover': {
                        color: theme.palette.primary.dark,
                      },
                    }}
                  >
                    {file.name || file.url}
                    <OpenInNew sx={{ fontSize: 14 }} />
                  </Link>
                </ListItemText>
              </ListItem>
            ))}
          </List>
        </>
      )}

      <Divider sx={{ mt: 3, mb: 2 }} />

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ borderRadius: "12px", textTransform: "none" }}
        >
          Cerrar
        </Button>
        <Button
          variant="contained"
          startIcon={
            task.status === "completed" ? (
              <RadioButtonUnchecked />
            ) : (
              <CheckCircle />
            )
          }
          onClick={handleStatusToggle}
          sx={{
            borderRadius: "12px",
            textTransform: "none",
            boxShadow: "none",
          }}
          color={task.status === "completed" ? "warning" : "success"}
        >
          {task.status === "completed" ? "Reabrir" : "Completar"}
        </Button>
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={() => {
            onClose();
            onEdit(task);
          }}
          sx={{
            borderRadius: "12px",
            textTransform: "none",
            boxShadow: "none",
          }}
        >
          Editar
        </Button>
      </Stack>
    </Paper>
  );
};

export default TaskDetail;